import React, { useEffect, useState } from 'react';

import { Table } from 'react-bootstrap';

import Banner from 'shared/banner';
import Button from 'shared/button';
import LoadingSpinner from 'components/loading_spinner';
import Modal from 'components/modal';
import { requiredFields, shouldDisplayRow } from './helpers';
import { getMerge } from '../api';
import { propertyAttributes } from 'bundles/research/index/research_constants';

import 'components/styles/merge_page.scss';
import { startCase } from 'lodash';

interface Props {
  deal: Record<string, any>;
  project: Record<string, any>;
  saving?: boolean;
  onClose: () => void;
  onMerge: (properties: Record<string, any>[], attributesMap: Record<string, number | null>) => void;
}

// TODO: Most of this UI is copied over from bundles/research/index/merge_properties.js
// We should refactor the merge action from the research properties index to use a modal instead of
// a separate page, and then we can refactor/reuse this component between both places.

export default function MergePropertiesModal({ deal, project, saving, onClose, onMerge }: Props) {
  const [properties, setProperties] = useState<Record<string, any>[]>([]);
  const [propertyDefaults, setPropertyDefaults] = useState<Record<string, any>>({});
  const fetchProperties = async () => {
    const { properties, propertyDefaults } = await getMerge([deal.researchProperty, project.edgeResearchProperty]);
    setProperties(properties);
    setPropertyDefaults(propertyDefaults);
  };
  const [attributesMap, setAttributesMap] = useState(
    propertyAttributes.reduce(
      (acc, key) => {
        acc[key] = null;
        return acc;
      },
      {} as Record<string, number | null>
    )
  );
  const rows: React.ReactNode[] = [];
  const hiddenKeys: string[] = [];

  useEffect(() => {
    if (!properties.length) {
      fetchProperties();
    }
  }, []);

  propertyAttributes.map((key) => {
    if (shouldDisplayRow(properties, propertyDefaults, key)) {
      const propertyCells: React.ReactNode[] = [];
      properties.map((property) => {
        const value = Array.isArray(property[key]) ? property[key].join(', ') : property[key];

        propertyCells.push(
          <td key={property.id + key}>
            <input
              checked={attributesMap[key] === property.id}
              className="checkbox--md"
              key={key}
              type="checkbox"
              value={property.id}
              onChange={() => setAttributesMap(prev => ({ ...prev, [key]: property.id }))}
            />
            <span className="left-buffer-sm"> {value} </span>
          </td>
        );
      });
      const id = attributesMap[key];
      const property = properties.find(prop => prop.id === id);
      const newVal = property ? property[key] : '';

      rows.push(
        <tr key={key}>
          <td>
            {startCase(key)}
            {requiredFields.includes(key) && <span className="required"> * </span> }
          </td>
          {propertyCells}
          <td> {newVal} </td>
        </tr>
      );
    } else {
      hiddenKeys.push(key);
    }
  });

  const selectedAll = (id) => {
    const selected = Object.keys(attributesMap).every((key) => {
      return hiddenKeys.includes(key) || attributesMap[key] === parseInt(id);
    });
    return selected;
  };

  const onSelectAll = (e) => {
    const id = parseInt(e.target.value);
    const newAttributesMap = {};

    Object.keys(attributesMap).map((key) => {
      newAttributesMap[key] = id;
    });

    setAttributesMap(newAttributesMap);
  };

  const propertyHeaders: React.ReactNode[] = [];
  properties.map((property, index) => {
    propertyHeaders.push(
      <th key={index}>
        <input
          checked={selectedAll(property.id)}
          className="checkbox--md"
          type="checkbox"
          value={property.id}
          onChange={onSelectAll}
        />
        <span className="left-buffer-md"> Property {index + 1} </span>
      </th>
    );
  });

  const bannerContent = (
    <div>
      <p>
        In the table below, you will see all conflicting information between the properties you have selected to merge.
        For each row, please select which data you would like to keep.
      </p>
      <p>
        Please note that all associations to contacts, comps, activities, etc. will be preserved.
      </p>
    </div>
  );

  const propertyAttributesTable = <>
    <Banner className="w-100 mb-5" content={bannerContent} variant="info" />
    <Table className="merge-table">
      <thead>
        <tr>
          <th> Fields </th>
          {propertyHeaders}
          <th> Merged Property </th>
        </tr>
      </thead>
      <tbody>
        {rows}
      </tbody>
    </Table>
    </>;

  return <>
    <Modal.Body className="d-flex flex-column flex-grow-1 justify-content-center align-items-center">
      { properties.length ? propertyAttributesTable : <LoadingSpinner /> }
    </Modal.Body>
    <Modal.Footer>
      <Button.Cancel onClick={onClose} />
      <Button.Primary
        disabled={!properties.length || saving || requiredFields.some(field => !attributesMap[field])}
        label="Merge"
        onClick={() => onMerge(properties, attributesMap)}
      />
    </Modal.Footer>
  </>;
}
